/**
 * Global error logging utility for production debugging
 */

interface ErrorDetails {
  message: string;
  stack?: string;
  timestamp: string;
  url: string;
  userAgent: string;
  type: 'javascript' | 'unhandledRejection' | 'react';
  additional?: any;
}

export class ErrorLogger {
  private static instance: ErrorLogger;
  private errors: ErrorDetails[] = [];

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  private constructor() {
    this.setupGlobalHandlers();
  }

  private setupGlobalHandlers() {
    // Handle unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.logError({
        message: event.message,
        stack: event.error?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        type: 'javascript',
        additional: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        }
      });
    });

    // Handle unhandled Promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        message: event.reason?.message || 'Unhandled Promise Rejection',
        stack: event.reason?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        type: 'unhandledRejection',
        additional: {
          reason: event.reason,
        }
      });
    });
  }

  logError(error: ErrorDetails) {
    this.errors.push(error);
    
    // Always log to console for Railway logs
    console.error('🚨 Application Error Logged:', error);
    
    // Keep only the last 50 errors to prevent memory leaks
    if (this.errors.length > 50) {
      this.errors = this.errors.slice(-50);
    }

    // In production, you could send to external service here
    if (process.env.NODE_ENV === 'production') {
      this.sendToExternalService(error);
    }
  }

  private sendToExternalService(error: ErrorDetails) {
    // Example: Send to your backend API for logging
    // fetch('/api/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(error)
    // }).catch(() => {}); // Ignore failures

    // For now, just ensure it's in console for Railway logs
    console.error('Production Error for External Service:', JSON.stringify(error, null, 2));
  }

  getRecentErrors(): ErrorDetails[] {
    return [...this.errors];
  }

  clearErrors() {
    this.errors = [];
  }
}

// Initialize error logger
export const errorLogger = ErrorLogger.getInstance();